import React, {useEffect, useState} from "react";
import SmartIcon from "../SmartIcon.tsx";
import {useProjectsStore} from "../../store/useProjectsStore.ts";
import {IProject} from "../../models/interfaces/project.interface.ts";
import {getSeriesReferenceFilesUploadUrls} from "../../services/seriesService.ts";
import Uppy, {UppyFile} from "@uppy/core";
import XHR from "@uppy/xhr-upload";
import {IUploadItem} from "../../models/interfaces/uploadItem.interface.ts";
import {FileProgressStarted} from "@uppy/utils/lib/FileProgress";
import SelectWithAutoComplete from "../SelectWithAutoComplete.tsx";
import {ISeriesUploadLink} from "../../models/interfaces/seriesUploadLink.interface.ts";
import AppSpinner from "../AppSpinner.tsx";
import {GenericMenuItem} from "../../models/interfaces/genericMenuItem.ts";
import useUiStore from "../../store/useUiStore.ts";

interface FileProjectCombination {
  file: File;
  project: IProject | null;
}

interface SeriesExternalFilesProps {
  seriesId: string;
  menuItems: GenericMenuItem[];
  setActiveItem: (item: GenericMenuItem) => void;
}

const SeriesExternalFiles: React.FC<SeriesExternalFilesProps> = (
  {
    seriesId,
    menuItems,
    setActiveItem,
  }: SeriesExternalFilesProps) => {
  const {showToast} = useUiStore()
  const {projects} = useProjectsStore();
  const [filesProjectsCombo, setFilesProjectsCombo] = useState<FileProjectCombination[]>([]);
  const [isProjectSelectorOpen, setIsProjectSelectorOpen] = useState<Record<string, boolean>>({});
  const [projectSelectorText, setProjectSelectorText] = useState<Record<string, string>>({});
  const [uploads, setUploads] = useState<IUploadItem[]>([]);
  const [uploadFileButtonDisabled, setUploadFileButtonDisabled] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const availableProjects: IProject[] = projects.filter((project: IProject) => {
    return filesProjectsCombo.filter((file: FileProjectCombination) => file.project?.id === project.id).length === 0;
  })

  const handleProjectSelection = (fileName: string, fileIndex: number, selectedProjectName: string) => {
    const selectedProject: IProject | undefined = availableProjects.find((project: IProject) => project.name === selectedProjectName);
    if (selectedProject) {
      setIsProjectSelectorOpen((prevState: Record<string, boolean>) => {
        return {...prevState, [fileName]: false};
      });
      setFilesProjectsCombo(filesProjectsCombo.map((file: FileProjectCombination, index: number) => {
        if (index === fileIndex) {
          const project: IProject | null = projects.find((p: IProject) => p.id === selectedProject.id) || null;
          return {...file, project};
        }
        return file;
      }));
      setProjectSelectorText((prevState: Record<string, string>) => {
        return {...prevState, [fileName]: selectedProject.name};
      })
    } else {
      setError("Selected project not found");
    }
  };

  const openFileSelector = () => {
    const input: HTMLInputElement = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.multiple = true;
    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const newFiles: File[] = Array.from(target.files);

        // Filter out files with duplicate names
        const uniqueNewFiles: File[] = newFiles.filter(
          (file: File) => !filesProjectsCombo.some((combo: FileProjectCombination) => combo.file.name === file.name)
        );

        if (uniqueNewFiles.length !== newFiles.length) {
          setError("One or more selected files are already added.");
        } else {
          setError(null);
        }

        // Append only unique files
        setFilesProjectsCombo((prev: FileProjectCombination[]) => [
          ...prev,
          ...uniqueNewFiles.map((file) => ({file, project: null}))
        ]);
        setUploads((prev: IUploadItem[]) => [
          ...prev,
          ...uniqueNewFiles.map((file) => ({
            fileName: file.name,
            progress: 0,
            status: "pending"
          }))
        ]);
      }
    };
    input.click();
  };

  const fetchSeriesReferenceFilesUploadUrls = async () => {
    setUploadFileButtonDisabled(true);
    const allFilesProjectIds: string[] = filesProjectsCombo.map((combo: FileProjectCombination) => combo.project?.id || "").filter((projectId: string) => projectId !== "");
    if (allFilesProjectIds.length === 0) {
      setError("Not all files are assigned to a project");
      setUploadFileButtonDisabled(false);
      return;
    }
    try {
      const uploadLinks: ISeriesUploadLink = await getSeriesReferenceFilesUploadUrls(seriesId, allFilesProjectIds);
      const uploadPromises: Promise<void>[] = filesProjectsCombo.map((combo: FileProjectCombination, i: number) => {
        const uploadUrl: { url: string, sasToken: string } = uploadLinks.urls[combo.project!.id];
        const endpoint: string = `${uploadUrl.url}?${uploadUrl.sasToken}`;

        return new Promise<void>((resolve, reject) => {
          const uppy = new Uppy();
          uppy.addFile({name: combo.file.name, type: combo.file.type, data: combo.file});

          uppy.use(XHR, {
            method: "PUT",
            formData: false,
            bundle: false,
            endpoint,
            headers: {
              "x-ms-blob-type": "BlockBlob",
              "x-ms-blob-content-type": combo.file.type,
            },
            getResponseData: () => ({}),
          });

          uppy.on(
            "upload-progress",
            (
              fileObj: UppyFile<Record<string, unknown>, Record<string, unknown>> | undefined,
              progress: FileProgressStarted
            ) => {
              if (!fileObj) return;
              const percentage: number = Math.round(
                (progress.bytesUploaded / (progress.bytesTotal ?? 1)) * 100
              );
              setUploads((prev: IUploadItem[]) =>
                prev.map((u: IUploadItem) =>
                  u.fileName === fileObj.name ? {...u, status: "inProgress", progress: percentage} : u
                )
              );
            }
          );

          (async () => {
            try {
              await uppy.upload();
              setUploads((prev: IUploadItem[]) =>
                prev.map((u: IUploadItem) =>
                  u.fileName === combo.file.name
                    ? {...u, status: "success", progress: 100}
                    : u
                )
              );

              setTimeout(() => {
                setUploads((prev: IUploadItem[]) =>
                  prev.filter((u: IUploadItem) => u.fileName !== combo.file.name)
                );
              }, 2000);
              resolve();
            } catch (err) {
              reject(err);
            }
          })();
        });
      });
    } catch (err) {
      setError("Failed to fetch upload URLs");
    } finally {
      setTimeout(() => {
        setUploadFileButtonDisabled(false)
        showToast("Files uploaded successfully", "success", "bottom-left");
        setActiveItem(menuItems[0])
      }, 1000)
    }
  }

  const removeUploadFileCandidate = (fileName: string, i: number) => {
    setFilesProjectsCombo(filesProjectsCombo.filter((_, index: number) => index !== i));
    setUploads((prev) => prev.filter((u) => u.fileName !== fileName));
  }

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 3000);
    return () => clearTimeout(timer);
  }, [error]);

  const isUploadDisabled: boolean = uploadFileButtonDisabled || filesProjectsCombo.some((combo: FileProjectCombination) => !combo.project);

  return (
    <div>
      <div className="flex items-center justify-between w-full">
        <div className="h4">External Files</div>
        <button
          className="p-2 text-sm border border-n-400 rounded-xl hover:border-brandPurple-500 hover:text-brandPurple-500 cursor-pointer active:scale-95 transition-transform"
          onClick={openFileSelector}
        >
          <span>+ Add File</span><span className="text-xs">(s)</span>
        </button>
      </div>
      <div className="relative pointer-events-auto">
        <div className="min-h-[400px] max-h-[500px] overflow-y-auto pl-2 pr-4">
          {filesProjectsCombo.map((file: { file: File, project: IProject | null }, i: number) => {
            const uploadItem: IUploadItem | undefined = uploads.find((u) => u.fileName === file.file.name);
            return (
              <div key={i} className="flex items-center justify-between mt-4  gap-4">
                <div className="text-sm mt-8">{file.file.name}</div>
                <SelectWithAutoComplete
                  isOpen={isProjectSelectorOpen[file.file.name] || false}
                  setIsOpen={(isOpen: boolean) => setIsProjectSelectorOpen(prevState => {
                    return {...prevState, [file.file.name]: isOpen};
                  })}
                  items={availableProjects.map((project: IProject) => project.name)}
                  selectedItem={projectSelectorText[file.file.name] || ""}
                  onSelectItem={(item: string) => handleProjectSelection(file.file.name, i, item)}
                  onChange={(value: string) => setProjectSelectorText(prevState => {
                    return {...prevState, [file.file.name]: value};
                  })}
                  placeholder="Select Project"
                  label="Project"
                />
                {uploadItem?.status == 'pending' ? (
                    <div
                      className="mt-6 cursor-pointer fill-bloodCrimson-400/80 hover:fill-bloodCrimson-400"
                      onClick={() => removeUploadFileCandidate(file.file.name, i)}
                    >
                      <SmartIcon
                        name="delete-chat"
                        className="w-5 h-5 "
                      />
                    </div>
                  )
                  : uploadItem?.status == 'inProgress' ? (
                    <AppSpinner size={"sm"} className="mt-6"/>
                  ) : (
                    <SmartIcon
                      name="check"
                      className="w-5 h-5 fill-green-500 mt-6"
                    />
                  )
                }
              </div>
            )
          })}
        </div>
      </div>
      {filesProjectsCombo.length > 0 && (
        <div className={`flex items-center mt-6 ${error ? 'justify-between' : 'justify-end'}`}>
          {error && (
            <div className="w-1/2 text-red-500">
              {error}
            </div>
          )}
          <button
            className={`min-w-[10rem] btn-purple ${isUploadDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={fetchSeriesReferenceFilesUploadUrls}
            disabled={isUploadDisabled}
          >
            {uploadFileButtonDisabled ? <AppSpinner size={"sm"}/> : "Upload Files"}
          </button>
        </div>
      )}
    </div>
  );
};

export default SeriesExternalFiles;